import {
	Directive,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { SatDatepickerComponent, SatDateRangeValue } from '../component/picker/sat-datepicker.component';

function pad2(n: number): string {
	return n < 10 ? `0${n}` : `${n}`;
}

function formatDate(date: any): string {
	const d = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(d.getTime())) return '';
	return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function formatRange(value: SatDateRangeValue): string {
	if (!value) return '';
	const { begin, end } = value as { begin: any; end: any };
	if (!begin || !end) return '';
	return `${formatDate(begin)} - ${formatDate(end)}`;
}

function isRangeValue(value: any): value is SatDateRangeValue {
	return !!value && typeof value === 'object' && 'begin' in value && 'end' in value;
}

@Directive({
	selector: 'input[satDatepicker]',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SatDatepickerInputDirective),
			multi: true,
		},
	],
})
export class SatDatepickerInputDirective implements ControlValueAccessor, OnChanges {
	@Input('satDatepicker') picker?: SatDatepickerComponent;
	@Output() dateChange = new EventEmitter<any>();

	@Input() min: unknown = null;
	@Input() max: unknown = null;
	@Input() matDatepickerFilter: ((date: unknown) => boolean) | null = null;

	private onChange: (value: any) => void = () => {};
	private onTouched: () => void = () => {};
	private modelValue: any = '';

	get disabled(): boolean {
		return this.elRef.nativeElement.disabled;
	}

	@Input()
	set value(val: any) {
		this.modelValue = val || '';
		this.renderToInput();
		this.picker?._setValue(this.modelValue);
	}

	get value(): any {
		return this.modelValue;
	}

	constructor(private readonly elRef: ElementRef<HTMLInputElement>) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['picker'] && this.picker) {
			this.picker._registerInput(this);
			this.picker._setValue(this.modelValue);
		}
		if (this.picker && (changes['min'] || changes['max'] || changes['matDatepickerFilter'])) {
			this.picker._markForCheck();
		}
	}

	writeValue(value: any): void {
		this.modelValue = value || '';
		this.renderToInput();
		this.picker?._setValue(this.modelValue);
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.elRef.nativeElement.disabled = isDisabled;
	}

	@HostListener('blur')
	onBlur(): void {
		this.onTouched();
	}

	@HostListener('input')
	onInput(): void {
		const inputValue = this.elRef.nativeElement.value?.trim() ?? '';
		if (inputValue === '') {
			this.modelValue = '';
			this.picker?._setValue('');
			this.onChange('');
			this.dateChange.emit({ target: { value: '' } });
		} else {
			this.picker?._setValue(this.modelValue);
			this.onChange(this.modelValue);
			this.dateChange.emit({ target: { value: this.modelValue } });
		}
	}

	onPickerSelectionChange(value: any): void {
		this.modelValue = value ?? '';
		this.renderToInput();
		this.onChange(this.modelValue);
		this.dateChange.emit({ target: { value: this.modelValue } });
	}

	private renderToInput(): void {
		const input = this.elRef.nativeElement;

		if (this.picker?.rangeMode) {
			input.value = formatRange(this.modelValue);
		} else {
			input.value = isRangeValue(this.modelValue) ? formatRange(this.modelValue) : formatDate(this.modelValue);
		}
	}
}
